/* eslint-disable complexity */
import { LDFlagSet } from 'launchdarkly-react-client-sdk';

import { CurrentUserCheck } from '@futrli/shared-services/authenticator/auth0/types';
import { LiveReportList } from '@futrli-sdk/predict';
import { PerformanceDashboard } from '@futrli-sdk/performance_dashboards';
import { WorkspaceMember } from '@futrli-sdk/people';

import { getUserName } from '../../utils/users/getUserName';

export interface DashboardActions {
  addToPerformanceArea: boolean;
  addToTemplates: boolean;
  delete: boolean;
  duplicate: boolean;
  edit: boolean;
  publish: boolean;
  removeFromPerformanceArea: boolean;
  share: boolean;
  unpublish: boolean;
}

export interface ExtendedLiveReportList extends LiveReportList {
  actions: DashboardActions;
  isOwner: boolean;
  isPerformanceDashboard: boolean;
  ownerName?: string;
}

export const processDashboard = (
  dashboard: LiveReportList,
  currentUser: CurrentUserCheck,
  members: WorkspaceMember[],
  performanceDashboards: PerformanceDashboard[],
  flags: LDFlagSet = {}
): ExtendedLiveReportList => {
  const hasResourceSharingEnabled = Boolean(flags.resourceSharing);
  const ownerId = hasResourceSharingEnabled ? dashboard.permission_info.owner_id : dashboard.owner_id;
  const isOwner = ownerId === currentUser.id;
  const effectivePermission = hasResourceSharingEnabled ? dashboard.permission_info.effective_permission : undefined;
  const hasViewPermission =
    !hasResourceSharingEnabled ||
    effectivePermission === 'view' ||
    effectivePermission === 'edit';
  const hasEditPermission = !hasResourceSharingEnabled || effectivePermission === 'edit';
  const isPerformanceDashboard = performanceDashboards.some(
    performanceDashboard => performanceDashboard.report_id === dashboard.id
  );
  const isPublished = dashboard.status === 'live';
  const owner = members.find(member => member.id === ownerId);
  const ownerName = owner ? getUserName(owner.name) : undefined;

  return {
    ...dashboard,
    isOwner,
    isPerformanceDashboard,
    ownerName,
    actions: {
      addToPerformanceArea:
        (hasResourceSharingEnabled ? hasEditPermission : isPublished) && !isPerformanceDashboard,
      addToTemplates: hasViewPermission,
      delete: hasEditPermission && (isOwner || !dashboard.is_locked),
      duplicate: hasViewPermission,
      edit: hasEditPermission && !dashboard.is_locked,
      publish: !hasResourceSharingEnabled && isOwner && !dashboard.is_locked && dashboard.status === 'draft',
      removeFromPerformanceArea:
        (hasResourceSharingEnabled ? hasEditPermission : isPublished) && isPerformanceDashboard,
      share: hasResourceSharingEnabled && hasEditPermission,
      unpublish: !hasResourceSharingEnabled && isOwner && !dashboard.is_locked && isPublished
    }
  };
};

export const processDashboards = (
  reports: LiveReportList[],
  currentUser: CurrentUserCheck,
  members: WorkspaceMember[],
  performanceDashboards: PerformanceDashboard[],
  flags: LDFlagSet = {}
): ExtendedLiveReportList[] =>
  reports
    .filter(report => report.orientation === 'dashboard')
    .map(dashboard => processDashboard(dashboard, currentUser, members, performanceDashboards, flags));
